import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';

/**
 * Moldura pra linha que rola na horizontal, com setas nas pontas enquanto houver
 * o que rolar de cada lado — o padrão da barra de abas do design hub
 * (`FaixaComSetas`). `scroll` + `ResizeObserver` decidem cada seta, porque seta
 * permanente em faixa que cabe é ruído; o fade sob cada seta diz que tem
 * conteúdo continuando por baixo dela.
 *
 * No ponteiro grosso (toque) setas e fade somem: o dedo já arrasta a faixa, e o
 * botão só encobria o último item (#6203). Ali quem diz "tem mais" é o item
 * cortado na borda — o hospedeiro deixa a faixa sangrar até ela.
 *
 * Contrato: o filho rolável é localizado por `scrollerSelector` DENTRO da
 * moldura e precisa de `overflow-x-auto` próprio.
 *
 * `surface` é a cor sobre a qual a faixa está: o esmaecimento sai dela, e um
 * fade de `card` sobre o fundo da página (escuro: sand-900 sob sand-800)
 * desenhava uma faixa clara em vez de sumir.
 */
export function ArrowStrip({
  scrollerSelector,
  previousLabel,
  nextLabel,
  surface = 'card',
  children,
}: {
  /** Ex.: '[data-slot="toggle-group"]'. */
  scrollerSelector: string;
  previousLabel: string;
  nextLabel: string;
  surface?: 'card' | 'background';
  children: React.ReactNode;
}) {
  const frame = useRef<HTMLDivElement>(null);
  const [arrows, setArrows] = useState({ left: false, right: false });

  useEffect(() => {
    const list = frame.current?.querySelector(scrollerSelector);
    if (!list) return undefined;
    const measure = () => {
      const left = list.scrollLeft > 4;
      const right = list.scrollLeft + list.clientWidth < list.scrollWidth - 4;
      /* Só troca o estado quando uma seta MUDA: `scroll` dispara por frame e um
         objeto novo por evento re-renderizava a moldura a rolagem inteira. */
      setArrows((current) =>
        current.left === left && current.right === right ? current : { left, right },
      );
    };
    /* Sem chamada síncrona aqui: a primeira medição vem da entrega inicial do
       `ResizeObserver` (observe() dispara com o estado atual), depois do commit
       da árvore — medir antes força layout no meio do commit. */
    list.addEventListener('scroll', measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    /* O `ResizeObserver` só vê a CAIXA: ligar um chip acrescenta o check e muda
       o `scrollWidth` sem mexer no `clientWidth` — sem re-medir por mutação, a
       faixa estoura com a seta ainda apagada. */
    const contentObserver = new MutationObserver(measure);
    contentObserver.observe(list, { childList: true, subtree: true, characterData: true });
    return () => {
      list.removeEventListener('scroll', measure);
      observer.disconnect();
      contentObserver.disconnect();
    };
  }, [scrollerSelector]);

  const scrollBy = (step: number) => {
    frame.current?.querySelector(scrollerSelector)?.scrollBy({ left: step, behavior: 'smooth' });
  };

  const fade = surface === 'card' ? 'from-card' : 'from-background';
  const arrow = (side: 'left' | 'right') => (
    <div
      className={`absolute inset-y-0 flex items-center pointer-coarse:hidden ${fade} ${
        side === 'left'
          ? 'left-0 bg-gradient-to-r to-transparent pr-6'
          : 'right-0 bg-gradient-to-l to-transparent pl-6'
      }`}
    >
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={side === 'left' ? previousLabel : nextLabel}
        className="bg-card ring-foreground/10 shadow-sm ring-1"
        onClick={() => {
          scrollBy(side === 'left' ? -160 : 160);
        }}
      >
        {side === 'left' ? <ChevronLeft aria-hidden /> : <ChevronRight aria-hidden />}
      </Button>
    </div>
  );

  return (
    <div ref={frame} className="relative min-w-0">
      {children}
      {arrows.left ? arrow('left') : null}
      {arrows.right ? arrow('right') : null}
    </div>
  );
}
